// token-grid.js
class TokenGrid extends HTMLElement {
  constructor() {
    super();
    this._maxLimit = parseInt(this.dataset.maxLimit, 10) || 1000;
    //console.log("Max limit is "+ this._maxLimit);
    this._productType = this.dataset.productType || "";
    this._isValidationPassed = false;
    this._counts = {};
  }

  connectedCallback() {
    this._wrapper   = this.querySelector('.token__wrapper');
    this._items     = Array.from(this.querySelectorAll('.token__item'));
    this._output    = this.querySelector('#token__output');
    this._handle_output    = this.querySelector('#token_handle__output');
    this._total     = this.querySelector('.token__total-count-no');
    this._maxText   = this.querySelector('.token__max-limit');
   

    this._items.forEach(item => this._bindItem(item));

    const filter = this.querySelector('#token-category-filter');
    if (filter) filter.addEventListener('change', () => this._applyFilter(filter.value));

    this.querySelectorAll('.token__category-header')
      .forEach(h => h.addEventListener('click', () =>
        h.closest('.token__category').classList.toggle('active')
      ));

    document.addEventListener('planner-token-renamed', e => this._cloneForRename(e.detail));
    document.addEventListener('token-removed', e => {
      const { id, count, internal } = e.detail || {};
      if (!internal) return;
      if (id && typeof count === 'number') {
        this._decrement(id, count);
        this._updateItemClass(id);
        this._updateDisplay();
      }
    });

    if (this._productType === "Weekly") {
      this._initAdvancedValidation();

      document.addEventListener('theme:cartchanged', () => {
        //console.log("Validation cart before =>", this._isValidationPassed);
        this._isValidationPassed = false;
        //console.log("Validation cart after  =>", this._isValidationPassed);
      });
    }

    document.addEventListener('token-selected', e => {
      // only handle the ones our planner just emitted
      if (e.detail.source === 'planner') {
        const { id, count = 1 } = e.detail;
        this._increment(id, count);
        this._updateDisplay();
      }
    });

    this._updateDisplay();
  }

  _bindItem(item) {
    const img = item.querySelector('.token__image-element');
    const id  = img.id;
    this._counts[id] = this._counts[id] || 0;

    item.querySelectorAll('.token__quantity-btn').forEach(btn => {
      btn.addEventListener('click', () => this._onQtyClick(id, btn));
    });

    img.addEventListener('dragstart', e => {
      // Extract background color from the token item's CSS variable
      const tokenItem = img.closest('.token__item');
      const bgColor = tokenItem ? getComputedStyle(tokenItem).getPropertyValue('--background-color').trim() || '#fff' : '#fff';
      
      e.dataTransfer.setData('application/json', JSON.stringify({
        id, image: img.src, name: img.dataset.tokenName, backgroundColor: bgColor
      }));
    });

    img.addEventListener('click', () => {
      if (this.total + 1 <= this._maxLimit) {
        this._increment(id, 1);
        this._dispatchSelect(id, 1);
      } else {
        this._showError(`You have reached the maximum token limit of ${this._maxLimit}.`);
      }
      this._updateDisplay();
    });

    const editBtn = item.querySelector('.token__edit-btn');
    const span    = item.querySelector('.token__text-label');
    const input   = item.querySelector('.token__text-input');
    const colorPicker = item.querySelector('.token__edit-color');
    const colorInput = item.querySelector('.token__bg-color-input');

    if (editBtn && span && input) {
      editBtn.addEventListener('click', e => {
        e.stopPropagation();
        span.style.display  = 'none';
        input.style.display = '';
        input.value = span.textContent.trim();
        input.focus();
        input.select();
        
        // Show color picker if it exists (only for tokens with background_color)
        if (colorPicker) {
          colorPicker.style.display = '';
        }
      });

      // Track if we're interacting with color picker
      let isInteractingWithColorPicker = false;
      
      if (colorPicker && colorInput) {
        // When clicking anywhere on the color picker container, focus the input
        colorPicker.addEventListener('mousedown', (e) => {
          // If clicking directly on the color input, let it handle normally
          if (e.target === colorInput || e.target === colorPicker) {
            isInteractingWithColorPicker = true;
            // If clicking on the container (not the input), focus the input
            if (e.target === colorPicker) {
              e.preventDefault();
              setTimeout(() => {
                colorInput.focus();
                colorInput.click();
              }, 10);
            }
          }
        });
        
        colorPicker.addEventListener('mouseup', () => {
          // Reset after a short delay to allow color picker to open
          setTimeout(() => {
            isInteractingWithColorPicker = false;
          }, 300);
        });
      }
      
      input.addEventListener('blur', (e) => {
        // Check if the focus is moving to the color picker
        const relatedTarget = e.relatedTarget;
        const isMovingToColorPicker = colorPicker && (
          relatedTarget === colorInput || 
          colorPicker.contains(relatedTarget) ||
          isInteractingWithColorPicker
        );
        
        // Only hide color picker if focus is not moving to the color picker
        if (!isMovingToColorPicker) {
          // Use setTimeout to allow color picker click to register first
          setTimeout(() => {
            // Double-check that color picker doesn't have focus and we're not interacting with it
            if (!isInteractingWithColorPicker && colorInput && document.activeElement !== colorInput) {
              if (colorPicker) {
                colorPicker.style.display = 'none';
              }
              this._finishGridEdit(id, span, input);
            }
          }, 200);
        }
      });
      

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();

          if (colorPicker) colorPicker.style.display = 'none';
          this._finishGridEdit(id, span, input);

          input.blur();
        }

        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          input.value = span.textContent.trim();
          if (colorPicker) colorPicker.style.display = 'none';
          span.style.display = '';
          input.style.display = 'none';
          input.blur();
        }
      });

      input.addEventListener('change', () => {
        if (colorPicker) colorPicker.style.display = 'none';
        finishOnce();
      });
    }
    
    // Handle color picker changes
    if (colorInput) {
      colorInput.addEventListener('change', e => {
        const newColor = e.target.value;
        this._updateTokenBackgroundColor(id, newColor);
      });
      
      colorInput.addEventListener('input', e => {
        const newColor = e.target.value;
        this._updateTokenBackgroundColor(id, newColor);
      });
      
      // Close color picker when it loses focus (user clicked outside)
      colorInput.addEventListener('blur', () => {
        if (colorPicker) {
          colorPicker.style.display = 'none';
        }
        // Also finish editing the text input if it's still visible
        if (input && input.style.display !== 'none') {
          this._finishGridEdit(id, span, input);
        }
      });
    }

    let isFinishingEdit = false;
    const finishOnce = () => {
      if (isFinishingEdit) return;
      isFinishingEdit = true;

      this._finishGridEdit(id, span, input);

      // allow next edit cycle
      setTimeout(() => { isFinishingEdit = false; }, 0);
    };
  }

  _onQtyClick(id, btn) {
    const delta = parseInt(btn.dataset.action, 10);
    if (delta > 0) {
      if (this.total + delta <= this._maxLimit) {
        this._increment(id, delta);
        this._dispatchSelect(id, delta);
      } else {
        this._showError(`You have reached the maximum token limit of ${this._maxLimit}.`);
      }
    } else {
      this._dispatchRemove(id, Math.abs(delta), false);
    }
    this._updateDisplay();
  }

  _increment(id, n = 1) {
    if (this.total + n > this._maxLimit) {
      return this._showError(`You have reached the maximum token limit of ${this._maxLimit}.`);
    }
    this._counts[id] += n;
    this._updateItemClass(id);
  }

  _decrement(id, n = 1) {
    this._counts[id] = Math.max(0, this._counts[id] - n);
    this._updateItemClass(id);
  }

  _updateItemClass(id) {
    // Only update all instances for non-weekly products
    if (this._productType === 'Weekly') {
      const item = this.querySelector(`#${id}`).closest('.token__item');
      if (item) {
        item.classList.toggle('token__item-added', this._counts[id] > 0);
        const input = item.querySelector('.token__quantity-input');
        if (input) input.value = this._counts[id];
      }
      return;
    }

    // For non-weekly: Update all instances of this token across all categories
    const items = Array.from(this.querySelectorAll(`#${id}`)).map(el => el.closest('.token__item'));
    items.forEach(item => {
      if (item) {
        item.classList.toggle('token__item-added', this._counts[id] > 0);
        const input = item.querySelector('.token__quantity-input');
        if (input) input.value = this._counts[id];
      }
    });
  }

  _dispatchSelect(id, count) {
    const img = this.querySelector(`#${id}`);
    const tokenItem = img.closest('.token__item');
    const bgColor = tokenItem ? getComputedStyle(tokenItem).getPropertyValue('--background-color').trim() || '#fff' : '#fff';
    
    this.dispatchEvent(new CustomEvent('token-selected', {
      detail: { id, image: img.src, name: img.dataset.tokenName, count, backgroundColor: bgColor },
      bubbles: true, composed: true
    }));
  }

  _dispatchRemove(id, count, internal = false) {
    this.dispatchEvent(new CustomEvent('token-removed', {
      detail: { id, count, internal },
      bubbles: true, composed: true
    }));
  }

  _finishGridEdit(oldId, span, input) {
    // ✅ prevents double-fire from blur + enter, etc.
    if (!input || input.style.display === 'none') return;

    const newName = input.value.trim() || span.textContent.trim();

    const tokenItem = input.closest('.token__item');
    const colorPicker = tokenItem?.querySelector('.token__edit-color');
    if (colorPicker) colorPicker.style.display = 'none';

    if (newName === span.textContent.trim()) {
      span.style.display = '';
      input.style.display = 'none';
      return;
    }

    const newId = `${oldId}-renamed-${Date.now()}`;
    span.style.display  = '';
    input.style.display = 'none';

    this.dispatchEvent(new CustomEvent('planner-token-renamed', {
      detail: { oldId, newId, newName, from: 'grid' },
      bubbles: true, composed: true
    }));
  }

  _cloneForRename({ oldId, newId, newName, from }) {
    // Get all instances of this token (for non-weekly, same token may appear in multiple categories)
    // For weekly planners, only use the first instance (original behavior)
    let origItems = Array.from(this.querySelectorAll(`.token__image-element#${oldId}`)).map(el => el.closest('.token__item')).filter(Boolean);
    
    // For weekly planners, only process the first instance
    if (this._productType === 'Weekly') {
      origItems = origItems.slice(0, 1);
    }
    
    if (!origItems.length) return;

    const oldCount = this._counts[oldId] || 0;

    // — decide how many to transfer —
    let transferCount;
    if (from === 'planner' && this._productType === 'Weekly') {
      // weekly + planner rename: only move one token
      transferCount = Math.min(1, oldCount);
    } else if (from === 'grid') {
      transferCount = 0;
    } else {
      // grid rename or non-weekly: move entire stack
      transferCount = oldCount;
    }

    // Clone and process each instance
    origItems.forEach(origItem => {
      const clone    = origItem.cloneNode(true);
      const img      = clone.querySelector('.token__image-element');

      // — update image element in clone —
      img.id                  = newId;
      img.dataset.tokenId     = newId;
      img.dataset.originalName= img.dataset.tokenName;
      img.dataset.rootName    = img.dataset.rootName || img.dataset.tokenName;
      img.dataset.tokenName   = newName;
      // Preserve the token color if it exists
      if (img.dataset.tokenColor) {
        img.dataset.tokenColor = img.dataset.tokenColor;
      }
      img.alt                 = newName;
      clone.querySelector('.token__text-label').textContent = newName;
      clone.querySelectorAll('.token__quantity-btn').forEach(b => b.dataset.id = newId);

      // — set up the clone's quantity input & internal count —
      const qtyInput = clone.querySelector('.token__quantity-input');
      qtyInput.className = `token__quantity-input ${newId}-quantity`;
      qtyInput.value     = transferCount;

      this._bindItem(clone);
      origItem.insertAdjacentElement('afterend', clone);
    });

    // — adjust counts on each ID —
    this._counts[newId] = transferCount;
    if (from !== 'grid') {
      this._counts[oldId] = oldCount - transferCount;
    }

    // — update the UI for both items —
    this._updateItemClass(oldId);
    this._updateItemClass(newId);
    this._updateDisplay();
  }

  _updateTokenBackgroundColor(tokenId, newColor) {
    // Update the token item's background color
    const tokenItem = this.querySelector(`#${tokenId}`)?.closest('.token__item');
    if (tokenItem) {
      tokenItem.style.setProperty('--background-color', newColor);
    }
    
    // Update the data-token-color attribute on the image element
    const img = this.querySelector(`#${tokenId}`);
    if (img) {
      img.dataset.tokenColor = newColor;
    }
    
    // Update all planner circles that have this token ID
    const plannerBoard = document.querySelector('planner-board');
    if (plannerBoard) {
      const circles = plannerBoard.querySelectorAll(`.planner__circle[data-token-id="${tokenId}"]`);
      circles.forEach(circle => {
        circle.style.setProperty('--background-color', newColor);
      });
    }
    
    // Dispatch event so other components can update if needed
    this.dispatchEvent(new CustomEvent('token-color-changed', {
      detail: { id: tokenId, backgroundColor: newColor },
      bubbles: true, composed: true
    }));
  }

  _updateDisplay() {
    this._total.textContent = `${this.total}`;
    if (this._output) {
      this._output.value = Object.entries(this._counts)
        .filter(([_, c]) => c > 0)
        .map(([id, c]) => {
          const el   = this.querySelector(`#${id}`);
          const name = el.dataset.tokenName;
          const root = el.dataset.rootName;
          const color = el.dataset.tokenColor;
          const colorPart = color ? `, color: ${color}` : '';
          return root && root !== name
            ? `✐ ${c} × ${name} (original: ${root}${colorPart})`
            : `✐ ${c} × ${name}${colorPart ? ` (color: ${color})` : ''}`;
        })
        .join('\n');
    }

    if (this._handle_output) {
      this._handle_output.value = Object.entries(this._counts)
        .filter(([_, c]) => c > 0)
        .map(([id, c]) => {
          const el     = this.querySelector(`#${id}`);
          const handle = el.dataset.tokenHandle;
          return `${c}×${handle}`;
        })
        .join('|');
    }

    if(this._productType != 'Weekly') {
      const totalQuantityElement = document.querySelector('.product-area--token input[aria-label="Quantity"]');
      totalQuantityElement.value = `${this.total}`;
      this._updateStickyATC();
    }
  }

  _applyFilter(slug) {
    this.querySelectorAll('.token__category').forEach(cat => {
      cat.style.display = (slug === 'all' || cat.dataset.category === slug) ? '' : 'none';
    });
  }

  get total() {
    return Object.values(this._counts).reduce((a, b) => a + b, 0);
  }

  /**
   * @function updateStickyATC
   * @description Updates the token count and price in the sticky ATC button
   */
  _updateStickyATC() {
    const stickyATC = document.querySelector('.footer-interactive-button-xs');
    if (!stickyATC) return;

    const tokenCount = stickyATC.querySelector('.token-count');
    const tokenPrice = stickyATC.querySelector('.token-price');

    let totalTokens = Object.values(this._counts).reduce((sum, c) => sum + c, 0);

    const priceElement = document.querySelector('.current-price.theme-money');
    const priceText = priceElement ? priceElement.textContent.trim() : '0';
    const productPrice = parseFloat(priceText.replace(/[^0-9.]/g, ''));

    tokenCount.textContent = `${totalTokens} token${totalTokens !== 1 ? 's' : ''}`;
    tokenPrice.textContent = `£${(totalTokens * productPrice).toFixed(2)}`;
  }

  // —————————————
  // Weekly-only advanced validation
  // —————————————

  _initAdvancedValidation() {
    this._getMaxTokenLimitFromVariants();
    const addBtn = document.querySelector('.product-purchase-form .button[type="submit"][name="add"]');
    if (!addBtn) return;

    addBtn.addEventListener('click', e => {
      //console.log("Validation checking " + this._isValidationPassed );
      if (this._isValidationPassed) return;
      e.preventDefault();

      const currentLimit   = this._getCurrentVariantLimit();
      const selectedCount  = this.total;

      // 1) blank planner: both zero → allow
      if (currentLimit === 0 && selectedCount === 0) {
        this._isValidationPassed = true;
         addBtn.click();
         //console.log("Validation check passed" + this._isValidationPassed );
         document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
        return;
      }

      // 2) none selected → error
      if (selectedCount === 0) {
        this._showError(`Please select at least one token or choose the "None" option.`);
        return;
      }

      // 3) too few → error
      if (selectedCount < currentLimit) {
        this._showError(`Please select at least ${currentLimit} tokens to match your chosen token amount.`);
        return;
      }

      // 4) too many → error
      if (selectedCount > currentLimit) {
        const nextHighest = this._getNextHighestVariantAmount(selectedCount);
        this._showError(
          `You have selected ${selectedCount} tokens, which is more than your chosen amount of ${currentLimit}. ` +
          `Please select ${nextHighest || 'a higher'} token count to proceed.`
        );
        return;
      }

      // 5) valid → submit
      this._isValidationPassed = true;
      addBtn.click();
      //console.log("Validation check passed" + this._isValidationPassed );
      document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
    });
  }

  _getMaxTokenLimitFromVariants() {
    let highest = window.maxTokenLimitFromVariants || 0;
    document.querySelectorAll('.options-token-count a').forEach(a => {
      const v = parseInt(a.dataset.value, 10);
      if (!isNaN(v) && v > highest) highest = v;
    });
    this._maxLimit = highest;
  }

  _getCurrentVariantLimit() {
    const select = document.querySelector('[data-option-name="Token Count"] select');
    if (!select) return 0;
    const val = select.value.trim().toLowerCase();
    if (val.includes('none')) return 0;
    const n = parseInt(select.value, 10);
    return isNaN(n) ? 0 : n;
  }

  _getNextHighestVariantAmount(count) {
    const opts = Array.from(document.querySelectorAll('.options-token-count a'))
                      .map(a => parseInt(a.dataset.value,10))
                      .filter(v => !isNaN(v))
                      .sort((a,b)=>a-b);
    for (let v of opts) if (v >= count) return v;
    return opts.length ? opts[opts.length-1] : null;
  }

  _showError(msg) {
    this._maxText.textContent = msg;
    this._maxText.classList.add('show');
    this._maxText.scrollIntoView({ behavior: 'smooth' });
  }
  
}

customElements.define('token-grid', TokenGrid);


// Fit-text registry: auto-scaling text elements based on container width
(function() {
  const registry = [];  // entries: { el, span, defaultText, fit, inputId }

  document.querySelectorAll("[data-fit-text]").forEach(el => {
    const defaultText = el.getAttribute("data-fit-default") || "";
    const inputId     = el.getAttribute("data-fit-input");
    let   span        = el.querySelector("span");

    // wrap in a span if needed
    if (!span) {
      span = document.createElement("span");
      span.textContent = defaultText || el.textContent.trim();
      el.textContent = "";
      el.appendChild(span);
    }

    // fit function specific to this element/span
    const fit = () => {
      span.style.transform = "scale(1)";
      const scale = Math.min(1, el.clientWidth / span.clientWidth);
      span.style.transform = `scale(${scale})`;
    };

    // observe size changes
    new ResizeObserver(fit).observe(el);

    // initial fit
    fit();

    registry.push({ el, span, defaultText, fit, inputId });
  });

  // wire inputs to update text
  registry.forEach(entry => {
    if (!entry.inputId) return;
    const input = document.getElementById(entry.inputId);
    if (!input) return;

    input.addEventListener("input", () => {
      entry.span.textContent = input.value.trim() || entry.defaultText;
      entry.fit();
    });
  });

  handleStickyAddToCart();

  /**
   * @function handleStickyAddToCart
   * @description Handles the click event for the sticky add to cart button
   */
  function handleStickyAddToCart() {
    const stickyAddToCart = document.querySelector('.product-area__add-to-cart button');
    if (stickyAddToCart) {
      stickyAddToCart.addEventListener('click', event => {
        event.preventDefault();
        const mainAddToCartButton = document.querySelector('.product-purchase-form button[type="submit"][name="add"]');
        if (mainAddToCartButton) mainAddToCartButton.click();
      });
    }
  }
})();
